import type { BehaviorStepType } from 'types/api/diagnosisReportRefResponseType';
import { formatNumberToCurrency } from 'utils/app';

import type { JSX } from 'react';

type DwellTarget = { pageNo: number; label: string; medianSec: number };

/**
 * ステップ別滞在時間表示コンポーネント（EV-report-analytics-03）。
 * 次のステップへ進むまでにかかった時間の中央値（秒）を横棒で表示する。
 * 最終ステップ（結果）は medianSec が null のため対象から自然に除外される。
 */
export default function WsReportStepDwellTime(props: {
  steps: BehaviorStepType[];
}): JSX.Element | null {
  const targets: DwellTarget[] = props.steps
    .filter((step): step is BehaviorStepType & { medianSec: number } => step.medianSec !== null)
    .map(step => ({ pageNo: step.pageNo, label: step.label, medianSec: step.medianSec }));

  if (targets.length === 0) return null;

  const maxMedianSec = Math.max(...targets.map(target => target.medianSec));
  let highlightIndex = 0;
  targets.forEach((target, index) => {
    if (target.medianSec > targets[highlightIndex].medianSec) highlightIndex = index;
  });

  return (
    <div className="card card-white behavior-analytics-card">
      <div className="card-body">
        <h4 className="title-section title-sm">ステップ別滞在時間</h4>
        {targets.map((target, index) => {
          const barWidthPercent = maxMedianSec === 0 ? 0 : (target.medianSec / maxMedianSec) * 100;
          const highlighted = index === highlightIndex;

          return (
            <div key={target.pageNo} className="behavior-funnel-row">
              <div className="behavior-funnel-row-head">
                <span className="behavior-funnel-row-label">{target.label}</span>
                <span
                  className={
                    highlighted
                      ? 'behavior-funnel-row-stats behavior-funnel-row-stats--highlight'
                      : 'behavior-funnel-row-stats'
                  }
                >
                  {formatNumberToCurrency(target.medianSec)}秒
                </span>
              </div>
              <div className="behavior-bar-track">
                <div
                  data-testid="behavior-dwell-bar-fill"
                  className={
                    highlighted
                      ? 'behavior-dwell-bar-fill behavior-dwell-bar-fill--highlight'
                      : 'behavior-dwell-bar-fill'
                  }
                  style={{ width: `${barWidthPercent}%` }}
                />
              </div>
            </div>
          );
        })}
        <p className="behavior-analytics-note">
          次のステップへ進むまでにかかった時間の中央値です。30
          分を超えた分は離席とみなして除外しています。
        </p>
        <p className="behavior-analytics-note">
          最終ステップ（結果）は次のステップが無いため滞在時間を算出できません。
        </p>
      </div>
    </div>
  );
}
